import { db } from './firebase.ts';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Centralized application settings
export interface AppSettings {
  timezone: {
    name: string;
    displayName: string;
    ianaTz: string;
  };
  dateFormat: {
    style: 'yyyy-mm-dd' | 'mm/dd/yyyy' | 'dd/mm/yyyy';
    separator: string;
  };
  timeFormat: {
    hours: 12 | 24;
    showSeconds: boolean;
  };
  calendar: {
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday, 1 = Monday, etc.
  };
  measurements: {
    system: 'metric' | 'imperial';
    currency: string;
    temperature: 'celsius' | 'fahrenheit';
    distance: 'kilometers' | 'miles';
    weight: 'kilograms' | 'pounds';
  };
  locale: {
    language: string;
    country: string;
    numberFormat: string;
  };
}

// Default application settings
export const DEFAULT_SETTINGS: AppSettings = {
  timezone: {
    name: 'EAT',
    displayName: 'East Africa Time',
    ianaTz: 'Africa/Nairobi'
  },
  dateFormat: {
    style: 'yyyy-mm-dd',
    separator: '-'
  },
  timeFormat: {
    hours: 24,
    showSeconds: false
  },
  calendar: {
    weekStartsOn: 1 // Monday
  },
  measurements: {
    system: 'metric',
    currency: 'USD',
    temperature: 'celsius',
    distance: 'kilometers',
    weight: 'kilograms'
  },
  locale: {
    language: 'en',
    country: 'KE', // Kenya
    numberFormat: 'en-KE'
  }
};

// Available timezone options
export const TIMEZONE_OPTIONS = [
  // Africa
  { value: 'Africa/Nairobi', label: 'East Africa Time (EAT)', offset: '+03:00' },
  { value: 'Africa/Cairo', label: 'Eastern European Time (EET)', offset: '+02:00' },
  { value: 'Africa/Lagos', label: 'West Africa Time (WAT)', offset: '+01:00' },
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time (SAST)', offset: '+02:00' },
  { value: 'Africa/Casablanca', label: 'Western European Time (WET)', offset: '+00:00' },
  
  // Europe
  { value: 'Europe/Oslo', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)', offset: '+00:00' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Rome', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Madrid', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Amsterdam', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Stockholm', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Zurich', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Vienna', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Warsaw', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Prague', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Budapest', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time (EET)', offset: '+02:00' },
  { value: 'Europe/Athens', label: 'Eastern European Time (EET)', offset: '+02:00' },
  { value: 'Europe/Bucharest', label: 'Eastern European Time (EET)', offset: '+02:00' },
  { value: 'Europe/Kiev', label: 'Eastern European Time (EET)', offset: '+02:00' },
  { value: 'Europe/Moscow', label: 'Moscow Standard Time (MSK)', offset: '+03:00' },
  { value: 'Europe/Istanbul', label: 'Turkey Time (TRT)', offset: '+03:00' },
  
  // Americas
  { value: 'America/New_York', label: 'Eastern Standard Time (EST)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'Central Standard Time (CST)', offset: '-06:00' },
  { value: 'America/Denver', label: 'Mountain Standard Time (MST)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'Pacific Standard Time (PST)', offset: '-08:00' },
  { value: 'America/Anchorage', label: 'Alaska Standard Time (AKST)', offset: '-09:00' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Standard Time (HST)', offset: '-10:00' },
  { value: 'America/Toronto', label: 'Eastern Standard Time (EST)', offset: '-05:00' },
  { value: 'America/Vancouver', label: 'Pacific Standard Time (PST)', offset: '-08:00' },
  { value: 'America/Mexico_City', label: 'Central Standard Time (CST)', offset: '-06:00' },
  { value: 'America/Sao_Paulo', label: 'Brasilia Time (BRT)', offset: '-03:00' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time (ART)', offset: '-03:00' },
  { value: 'America/Lima', label: 'Peru Time (PET)', offset: '-05:00' },
  { value: 'America/Bogota', label: 'Colombia Time (COT)', offset: '-05:00' },
  { value: 'America/Caracas', label: 'Venezuela Time (VET)', offset: '-04:00' },
  { value: 'America/Santiago', label: 'Chile Standard Time (CLT)', offset: '-04:00' },
  
  // Asia
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)', offset: '+09:00' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)', offset: '+08:00' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (HKT)', offset: '+08:00' },
  { value: 'Asia/Singapore', label: 'Singapore Standard Time (SGT)', offset: '+08:00' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time (KST)', offset: '+09:00' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (ICT)', offset: '+07:00' },
  { value: 'Asia/Jakarta', label: 'Western Indonesia Time (WIB)', offset: '+07:00' },
  { value: 'Asia/Manila', label: 'Philippine Standard Time (PST)', offset: '+08:00' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia Time (MYT)', offset: '+08:00' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Indochina Time (ICT)', offset: '+07:00' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)', offset: '+05:30' },
  { value: 'Asia/Karachi', label: 'Pakistan Standard Time (PKT)', offset: '+05:00' },
  { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (BST)', offset: '+06:00' },
  { value: 'Asia/Kathmandu', label: 'Nepal Time (NPT)', offset: '+05:45' },
  { value: 'Asia/Colombo', label: 'Sri Lanka Standard Time (SLST)', offset: '+05:30' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)', offset: '+04:00' },
  { value: 'Asia/Tehran', label: 'Iran Standard Time (IRST)', offset: '+03:30' },
  { value: 'Asia/Baghdad', label: 'Arabia Standard Time (AST)', offset: '+03:00' },
  { value: 'Asia/Riyadh', label: 'Arabia Standard Time (AST)', offset: '+03:00' },
  { value: 'Asia/Jerusalem', label: 'Israel Standard Time (IST)', offset: '+02:00' },
  { value: 'Asia/Beirut', label: 'Eastern European Time (EET)', offset: '+02:00' },
  { value: 'Asia/Ankara', label: 'Turkey Time (TRT)', offset: '+03:00' },
  
  // Australia & Oceania
  { value: 'Australia/Sydney', label: 'Australian Eastern Standard Time (AEST)', offset: '+10:00' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Standard Time (AEST)', offset: '+10:00' },
  { value: 'Australia/Brisbane', label: 'Australian Eastern Standard Time (AEST)', offset: '+10:00' },
  { value: 'Australia/Perth', label: 'Australian Western Standard Time (AWST)', offset: '+08:00' },
  { value: 'Australia/Adelaide', label: 'Australian Central Standard Time (ACST)', offset: '+09:30' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (NZST)', offset: '+12:00' },
  { value: 'Pacific/Fiji', label: 'Fiji Time (FJT)', offset: '+12:00' },
  
  // UTC and International
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)', offset: '+00:00' },
  { value: 'GMT', label: 'Greenwich Mean Time (GMT)', offset: '+00:00' }
];

// Date format options
export const DATE_FORMAT_OPTIONS = [
  { value: 'yyyy-mm-dd', label: 'YYYY-MM-DD (ISO)', example: '2024-01-15' },
  { value: 'mm/dd/yyyy', label: 'MM/DD/YYYY (US)', example: '01/15/2024' },
  { value: 'dd/mm/yyyy', label: 'DD/MM/YYYY (EU)', example: '15/01/2024' }
];

// Week start options
export const WEEK_START_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 6, label: 'Saturday' }
];

// Measurement system options
export const MEASUREMENT_SYSTEM_OPTIONS = [
  { value: 'metric', label: 'Metric (km, kg, °C)' },
  { value: 'imperial', label: 'Imperial (miles, lbs, °F)' }
];

// Global settings reference for components that can't access context
let globalSettings: AppSettings = { ...DEFAULT_SETTINGS };

// Update global settings (called by context)
export const setGlobalSettings = (settings: AppSettings): void => {
  globalSettings = { ...settings };
};

// Settings getter
export const getSettings = (): AppSettings => {
  return { ...globalSettings };
};

// Settings setter
export const updateSettings = async (newSettings: Partial<AppSettings>, userId: string): Promise<void> => {
  const updatedSettings = {
    ...globalSettings,
    ...newSettings
  };
  globalSettings = updatedSettings;
  
  // Save to Firestore
  try {
    const settingsRef = doc(db, 'userSettings', userId);
    await setDoc(settingsRef, updatedSettings, { merge: true });
  } catch (error) {
    console.error('Failed to save settings to Firestore:', error);
    throw error;
  }
};

// Load settings from Firestore
export const loadSettings = async (userId: string): Promise<AppSettings> => {
  try {
    const settingsRef = doc(db, 'userSettings', userId);
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      const stored = settingsDoc.data() as AppSettings;
      const settings = {
        ...DEFAULT_SETTINGS,
        ...stored
      };
      globalSettings = settings;
      return settings;
    } else {
      // If no settings exist, create default settings
      await setDoc(settingsRef, DEFAULT_SETTINGS);
      globalSettings = { ...DEFAULT_SETTINGS };
      return globalSettings;
    }
  } catch (error) {
    console.error('Failed to load settings from Firestore:', error);
    globalSettings = { ...DEFAULT_SETTINGS };
    return globalSettings;
  }
};

// Initialize settings
export const initializeSettings = async (userId: string): Promise<AppSettings> => {
  return loadSettings(userId);
};

// Utility functions for common settings access
export const getCurrentTimezone = (): string => {
  return globalSettings.timezone.ianaTz;
};

export const getCurrentDateFormat = (): string => {
  return globalSettings.dateFormat.style;
};

export const getCurrentTimeFormat = (): number => {
  return globalSettings.timeFormat.hours;
};

export const getWeekStartDay = (): number => {
  return globalSettings.calendar.weekStartsOn;
};

export const getMeasurementSystem = (): 'metric' | 'imperial' => {
  return globalSettings.measurements.system;
};

export const getCurrency = (): string => {
  return globalSettings.measurements.currency;
};

export const getLocale = (): string => {
  return globalSettings.locale.numberFormat;
};

// Reset to defaults
export const resetSettingsToDefaults = async (userId: string): Promise<void> => {
  globalSettings = { ...DEFAULT_SETTINGS };
  try {
    const settingsRef = doc(db, 'userSettings', userId);
    await setDoc(settingsRef, DEFAULT_SETTINGS);
  } catch (error) {
    console.error('Failed to reset settings in Firestore:', error);
    throw error;
  }
}; 